import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthStore from '../../store/useAuthStore';
import toast from 'react-hot-toast';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { LockClosedIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const userType = searchParams.get('userType') || 'builder'; // Default to builder if not specified
    const navigate = useNavigate();
    const { 
        verifyResetToken, 
        resetPassword,
        customerVerifyResetToken,
        customerResetPassword
    } = useAuthStore();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isResetting, setIsResetting] = useState(false);
    const [verificationError, setVerificationError] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const checkToken = async () => {
            setIsLoading(true);
            setVerificationError('');
            setIsTokenValid(false);

            if (!token) {
                setVerificationError('Invalid or missing reset token.');
                setIsLoading(false);
                toast.error('Invalid or missing reset token.');
                return;
            }

            try {
                const isValid = userType === 'customer'
                    ? await customerVerifyResetToken(token)
                    : await verifyResetToken(token);

                setIsTokenValid(isValid);
                if (!isValid) {
                    setVerificationError('Invalid or expired reset token. Please request a new one.');
                    toast.error('Invalid or expired reset token.');
                }
            } catch (error) {
                setIsTokenValid(false);
                setVerificationError('Failed to verify token. Please try again or request a new link.');
                console.error("Token verification error:", error);
            } finally {
                setIsLoading(false);
            }
        };

        checkToken();
    }, [token, userType, verifyResetToken, customerVerifyResetToken]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match.');
            return;
        }
        if (newPassword.length < 6) {
             toast.error('Password must be at least 6 characters long.');
             return;
        }

        setIsResetting(true);
        try {
            const success = userType === 'customer'
                ? await customerResetPassword(token, newPassword)
                : await resetPassword(token, newPassword);

            if (success) {
                toast.success('Password reset successfully! You can now log in.');
                navigate(userType === 'customer' ? '/login?type=customer' : '/login?type=builder');
            }
        } catch (error) {
            console.error("Password reset submission failed:", error);
        } finally {
            setIsResetting(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center p-4 relative"
            style={{ 
                backgroundImage: "url('/assets/login.jpg')", 
                backgroundSize: 'cover', 
                backgroundPosition: 'center',
            }}
        >
            {/* Black overlay with 80% opacity */}
            <div className="absolute inset-0 bg-black opacity-50 z-0"></div>
            <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-2xl border border-gray-300 relative z-10">
                <h2 className="text-2xl font-semibold text-center text-gray-800 mb-6">
                    Reset Password
                </h2>

                {isLoading && (
                    <div className="flex justify-center items-center py-6">
                        <AiOutlineLoading3Quarters className="animate-spin text-3xl text-blue-500" />
                        <p className="ml-3 text-gray-600">Verifying link...</p>
                    </div>
                )}

                {verificationError && !isLoading && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded-md text-center">
                        <p>{verificationError}</p>
                         <button
                            onClick={() => navigate(userType === 'customer' ? '/customer/forgot-password' : '/forgot-password')}
                            className="mt-2 text-red-700 underline font-medium hover:text-red-800"
                        >
                            Request a new link?
                        </button>
                    </div>
                )}

                {isTokenValid && !isLoading && !verificationError && (
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label
                                htmlFor="newPassword"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                New Password
                            </label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                    <LockClosedIcon className="w-5 h-5 text-black" />
                                </span>
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    id="newPassword"
                                    name="newPassword"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full px-10 py-2 border rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    style={{ backgroundColor: "#dddddd", borderRadius: "12px" }}
                                    placeholder="Enter new password (min 6 chars)"
                                    required
                                    minLength={6}
                                    disabled={isResetting}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                                >
                                    {showNewPassword ? <EyeSlashIcon className="h-5 w-5"/> : <EyeIcon className="h-5 w-5"/>}
                                </button>
                            </div>
                        </div>

                        <div className="mb-6">
                             <label
                                htmlFor="confirmPassword"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                    <LockClosedIcon className="w-5 h-5 text-black" />
                                </span>
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full px-10 py-2 border rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    style={{ backgroundColor: "#dddddd", borderRadius: "12px" }}
                                    placeholder="Confirm new password"
                                    required
                                    minLength={6}
                                    disabled={isResetting}
                                />
                                 <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                >
                                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5"/> : <EyeIcon className="h-5 w-5"/>}
                                </button>
                             </div>
                        </div>

                        <div className="flex flex-col items-center">
                            <button
                                type="submit"
                                disabled={isResetting || !isTokenValid}
                                className={`w-full sm:w-auto px-6 py-2 mt-2 text-black border-2 transition-all duration-300 flex items-center justify-center ${
                                    (isResetting || !isTokenValid) ? "opacity-70 cursor-not-allowed" : "hover:bg-gray-50"
                                }`}
                                style={{
                                    background: "linear-gradient(310deg, #dddddd 10%, white)",
                                    borderRadius: "24px",
                                }}
                            >
                                {isResetting ? (
                                    <>
                                        <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                                        Resetting...
                                    </>
                                ) : (
                                    "Reset Password"
                                )}
                            </button>
                        </div>
                    </form>
                )}

                {!isLoading && !isTokenValid && (
                      <div className="flex justify-center mt-4">
                         <button
                            type="button"
                            onClick={() => navigate(userType === 'customer' ? '/login?type=customer' : '/login?type=builder')}
                            className="text-sm text-blue-600 hover:underline"
                         >
                             Back to Login
                         </button>
                     </div>
                 )}
            </div>
        </div>
    );
};

export default ResetPasswordPage;